<template>
    <div :class="getDeliveryMethodClass(method.key)">
        <div class="praise-modal__delivery__method__icon">
            <img
                v-if="method.svgIcon"
                :src="getDeliveryMethodLogo(method.key)"
                alt="method icon"
            />
            <FontAwesomeIcon v-else-if="method.faIcon" :icon="method.faIcon" />
        </div>

        <div class="praise-modal__delivery__method__content">
            <label
                v-tooltip.top="method.toolTipMsg"
                :for="method.key"
                class="praise-modal__delivery__method__label"
            >
                {{ method.label }}
            </label>
            <!--Extra Input Fields-->
            <slot />
        </div>
        <RadioButton
            v-model="selectedSendMethod"
            class="praise-modal__delivery__method__radio-button"
            :value="method.key"
            :input-id="method.key"
            name="method"
            @update:model-value="
                (value) => $emit('change', value, method.label)
            "
        />
    </div>
</template>

<script setup lang="ts">
import RadioButton from 'primevue/radiobutton'
import { FontAwesomeIcon } from 'fontawesome/vue-fontawesome'
import MicrosoftTeamLogo from '@/assets/img/Icons/microsoft-teams.svg'
import SlackLogo from '@/assets/img/Icons/slack.svg'
import {
    SEND_METHOD_SLACK,
    SEND_METHOD_TEAMS,
    SendMethod,
} from '@/entities/frontlineManagerDash'

type Props = {
    method: SendMethod
}

const props = defineProps<Props>()
const emit = defineEmits(['change'])
const selectedSendMethod = defineModel('selectedSendMethod')

const getDeliveryMethodClass = (key: string) => {
    return [
        'praise-modal__delivery__method',
        selectedSendMethod.value === key
            ? 'praise-modal__delivery__method--selected'
            : '',
    ]
}

const getDeliveryMethodLogo = (key: string) => {
    if (key === SEND_METHOD_SLACK) {
        return SlackLogo
    }

    if (key === SEND_METHOD_TEAMS) {
        return MicrosoftTeamLogo
    }

    return null
}
</script>

<style lang="less" scoped>
@import '@/styles/prime-vue/variables.less';
@import '@/styles/rain/variables.less';

.praise-modal__delivery {
    &__method {
        display: flex;
        gap: @spacing-lg;
        align-items: center;
        padding: @spacing-lg;
        border-radius: @radius-xl;
        border: 2px solid var(--p-gray-200);

        &__content {
            display: flex;
            flex-direction: column;
            flex: 1;
            gap: @spacing-xs;
        }

        &--selected {
            border: 2px solid var(--p-primary-500);
        }

        &__icon {
            width: 20px;
            height: 20px;
            padding: @spacing-sm;
            border-radius: 999px;
            background: var(--p-primary-100);
            color: var(--p-primary-500);
            flex-shrink: 0;

            svg,
            img {
                width: 100%;
                height: 100%;
            }
        }

        &__radio-button {
            flex-shrink: 0;
        }

        &__label {
            font-size: @fontSize-base;
            line-height: @lineHeight-base;
            color: var(--p-gray-700);
            font-weight: @fontWeight-medium;
        }

        :deep(.p-radiobutton-box) {
            box-sizing: border-box;
        }
        :deep(.p-radiobutton-icon) {
            width: 8px;
            height: 8px;
        }
    }
}

@media (min-width: @tabletBreakpoint) {
    .praise-modal__delivery {
        &__method {
            padding: @spacing-md @spacing-xl;
        }
    }
}
</style>
